// Product analytics for "warnings on route", with the same event names and
// properties on web, iOS and Android (ROUTE-ALERTS.md), sent through the
// `window.mixpanel` facade that routes to PostHog:
//
//   route_alerts_shown  { count, kinds, screen }               — the warnings appeared (count > 0)
//   route_alerts_opened { count, screen }                      — section expanded / sheet opened / chip clicked
//   route_alert_tapped  { kind, hazard_type, level, screen, more_count } — a hazard card clicked
//   route_alert_tapped  { kind, sac_grade, level, screen, more_count }   — a difficult section's card clicked
//   (the card's first alert; more_count = how many more of its type / grade the route has)
//   route_alert_paged   { kind, hazard_type | sac_grade, screen, position, total, direction }
//   (an arrow of the pager through a card's group, ‹ 1 / 3 ›: position is where it landed,
//    1-based; direction is next | previous)
//
// screen ∈ planner | hike_overview | map_hike_panel | hike_map (the hike's map page)
//   | map_hike_card (the round button beside a map's selected-hike card; it sends
//   no route_alerts_shown — the card's chip already does, as map_hike_panel).
//
// Values are lower-case snake_case, as the hazard events send them
// (`hazard`, `difficult_terrain`, `tree_down`, `danger`); `sac_grade` is the
// number 3-6; `platform: 'web'` as on hazard_viewed.

import { RouteAlert, RouteAlerts } from './routeAlertsModel';

export const ROUTE_ALERTS_SHOWN_EVENT = 'route_alerts_shown';
export const ROUTE_ALERTS_OPENED_EVENT = 'route_alerts_opened';
export const ROUTE_ALERT_TAPPED_EVENT = 'route_alert_tapped';
export const ROUTE_ALERT_PAGED_EVENT = 'route_alert_paged';

/** Where the warnings are: the planner, the hike page, the map's hike card, the hike's map page. */
export type RouteAlertsScreen = 'planner' | 'hike_overview' | 'map_hike_panel' | 'hike_map' | 'map_hike_card';

export interface RouteAlertsAnalyticsEvent {
  event: string;
  props: Record<string, unknown>;
}

/** The distinct kinds in the list, sorted: `['difficult_terrain', 'hazard']`. */
export function alertKinds(alerts: RouteAlerts): string[] {
  return Array.from(new Set(alerts.alerts.map((alert) => alert.rawKind))).sort();
}

export function routeAlertsShownEvent(alerts: RouteAlerts, screen: RouteAlertsScreen): RouteAlertsAnalyticsEvent {
  return {
    event: ROUTE_ALERTS_SHOWN_EVENT,
    props: { count: alerts.count, kinds: alertKinds(alerts), screen, platform: 'web' },
  };
}

export function routeAlertsOpenedEvent(count: number, screen: RouteAlertsScreen): RouteAlertsAnalyticsEvent {
  return { event: ROUTE_ALERTS_OPENED_EVENT, props: { count, screen, platform: 'web' } };
}

/** What a warning is, for its events: its hazard type, or a section's SAC grade (as on iOS and Android). */
function alertSubject(alert: RouteAlert): Record<string, unknown> {
  return alert.kind === 'difficult_terrain'
    ? { sac_grade: alert.sacGrade }
    : { hazard_type: alert.hazard?.rawHazardType ?? null };
}

export function routeAlertTappedEvent(alert: RouteAlert, screen: RouteAlertsScreen, moreCount = 0): RouteAlertsAnalyticsEvent {
  const subject = alertSubject(alert);
  return {
    event: ROUTE_ALERT_TAPPED_EVENT,
    props: {
      kind: alert.rawKind,
      ...subject,
      level: alert.level,
      screen,
      more_count: moreCount > 0 ? moreCount : 0,
      platform: 'web',
    },
  };
}

/** An arrow of the pager: `alert` is the one it landed on, `position` its place (1-based) of `total`. */
export function routeAlertPagedEvent(
  alert: RouteAlert,
  screen: RouteAlertsScreen,
  position: number,
  total: number,
  direction: 'next' | 'previous',
): RouteAlertsAnalyticsEvent {
  return {
    event: ROUTE_ALERT_PAGED_EVENT,
    props: { kind: alert.rawKind, ...alertSubject(alert), screen, position, total, direction, platform: 'web' },
  };
}

/**
 * Whether the planner sends route_alerts_shown for this result: once per
 * planning session when warnings first appear, then again only when the count
 * changes (not for every refetch of the same route, nor for zero).
 */
export function shouldTrackPlannerShown(count: number, lastTrackedCount: number | null): boolean {
  return count > 0 && count !== lastTrackedCount;
}

/** Fire-and-forget; a missing analytics shim must never break the page. */
export function trackRouteAlertsEvent({ event, props }: RouteAlertsAnalyticsEvent): void {
  try {
    (window as any).mixpanel?.track(event, props);
  } catch {
    // Telemetry must never break the flow it is reporting on.
  }
}
